<template>
  <div class="brand-analytics-page">
    <section class="hero">
      <div class="container hero-grid">
        <div class="hero-copy">
          <p class="eyebrow">BAILYHANGA / Brand Analytics</p>
          <h1>{{ texts.title }}</h1>
          <p class="lead">{{ texts.lead }}</p>
          <div class="hero-actions">
            <a class="action-link primary" :href="contactHref">{{ texts.contactLink }}</a>
            <router-link :to="{ name: 'spApiComplianceDetail' }" class="action-link">
              {{ texts.complianceLink }}
            </router-link>
          </div>
        </div>
        <div class="analytics-visual" aria-hidden="true">
          <div class="visual-head"><span>BRAND / SIGNALS</span><span class="status-dot">LIVE</span></div>
          <div class="metric-row">
            <div><span>SEARCH</span><strong>68.4</strong></div>
            <div><span>REPEAT</span><strong>42.7</strong></div>
            <div><span>BASKET</span><strong>31.6</strong></div>
          </div>
          <div class="chart-area">
            <span v-for="height in chartBars" :key="height" :style="{ height: `${height}%` }"></span>
          </div>
          <div class="visual-foot"><span>01</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
        </div>
      </div>
      <div class="container audience-strip">
        <div class="section-label"><span>01</span>{{ texts.audienceTitle }}</div>
        <ul><li v-for="item in texts.audienceItems" :key="item">{{ item }}</li></ul>
      </div>
    </section>

    <main>
      <section class="features-section">
        <div class="container section-grid">
          <div class="section-heading">
            <div class="section-label"><span>02</span>Capabilities</div>
            <h2>{{ texts.featureTitle }}</h2>
          </div>
          <div class="feature-list">
            <article v-for="(item, index) in texts.featureItems" :key="item.title" class="feature-item">
              <span class="item-index">0{{ index + 1 }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="reports-section">
        <div class="container reports-grid">
          <div class="report-copy">
            <div class="section-label light"><span>03</span>Data Scope</div>
            <h2>{{ texts.reportTitle }}</h2>
            <p>{{ texts.reportLead }}</p>
            <p class="note">{{ texts.reportNote }}</p>
          </div>
          <ol class="report-list">
            <li v-for="(item, index) in texts.reportItems" :key="item">
              <span>0{{ index + 1 }}</span><code>{{ cleanReportName(item) }}</code>
            </li>
          </ol>
        </div>
      </section>

      <section class="boundary-section">
        <div class="container">
          <div class="boundary-heading">
            <div class="section-label"><span>04</span>Data Principles</div>
            <h2>{{ texts.boundaryTitle }}</h2>
          </div>
          <div class="boundary-grid">
            <article v-for="(item, index) in texts.boundaryItems" :key="item.title" class="boundary-item">
              <span>0{{ index + 1 }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="usage-section">
        <div class="container usage-grid">
          <div>
            <div class="section-label"><span>05</span>In Practice</div>
            <h2>{{ texts.usageTitle }}</h2>
          </div>
          <ul class="usage-list">
            <li v-for="(item, index) in texts.usageItems" :key="item"><span>0{{ index + 1 }}</span>{{ item }}</li>
          </ul>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { localeState } from "@/utils/locale";

export default {
  name: "BrandAnalytics",
  data() {
    return {
      chartBars: [32, 44, 38, 57, 51, 68, 62, 78, 71, 86, 74, 92],
      copy: {
        zh: {
          title: "亚马逊品牌分析功能说明",
          lead:
            "GO GLOBAL CREDIT LIMTED为已授权的亚马逊品牌卖家和品牌所有者提供品牌经营分析工具，用于查看品牌搜索表现、复购表现、市场篮子关系和经营趋势，辅助品牌团队做广告优化、选品决策和内容运营。",
          contactLink: "联系我们",
          complianceLink: "查看 SP-API 合规说明",
          contactSubject: "Brand Analytics 功能咨询",
          audienceTitle: "适用对象",
          audienceItems: [
            "已完成品牌备案并具备相应数据权限的亚马逊卖家",
            "品牌所有者及其授权运营团队",
            "经客户明确授权后代为处理数据的服务团队",
          ],
          featureTitle: "我们提供的品牌分析功能",
          featureItems: [
            {
              title: "搜索词与搜索目录表现",
              desc:
                "帮助品牌团队查看品牌商品在搜索场景下的曝光、点击、转化和目录表现，支持分析核心关键词、核心 ASIN 和流量变化趋势。",
            },
            {
              title: "市场篮子与替代购买分析",
              desc:
                "用于识别消费者联购关系、替代购买关系和关联商品机会，帮助品牌进行组合销售、关联搭配和竞品策略分析。",
            },
            {
              title: "复购与客户粘性分析",
              desc:
                "用于观察重复购买情况、回购节奏和品牌客户留存趋势，为会员运营、复购活动和生命周期营销提供支持。",
            },
            {
              title: "品牌经营报表与内部看板",
              desc:
                "将品牌分析数据整合进内部经营看板，用于品牌经理、运营和广告团队的日常复盘与决策，不面向公众提供开放数据查询服务。",
            },
          ],
          reportTitle: "与 Brand Analytics 角色对应的数据使用场景",
          reportLead:
            "在客户具备相应资格并完成明确授权后，我们可能基于 Amazon SP-API 的 Brand Analytics 相关数据类型，为客户提供以下分析能力：",
          reportItems: [
            "`GET_BRAND_ANALYTICS_SEARCH_QUERY_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_SEARCH_CATALOG_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_MARKET_BASKET_REPORT`",
            "`GET_BRAND_ANALYTICS_REPEAT_PURCHASE_REPORT`",
          ],
          reportNote: "实际使用范围以 Amazon 对客户资格、应用角色和授权状态的要求为准。",
          boundaryTitle: "授权方式与数据边界",
          boundaryItems: [
            {
              title: "明确授权后访问",
              desc:
                "仅在客户完成授权后访问其品牌分析数据，不会在未授权状态下采集或查看相关信息。",
            },
            {
              title: "仅服务授权客户",
              desc:
                "数据仅用于该客户自身品牌运营和内部管理，不会用于对外分发的公共数据产品。",
            },
            {
              title: "不出售不转让",
              desc:
                "不会出售、出租、转让、许可或公开披露通过 Amazon SP-API 获取的品牌分析数据。",
            },
            {
              title: "不做跨客户公开聚合",
              desc:
                "不会将多个卖家或多个品牌的数据汇总后向公众展示、售卖或提供开放下载。",
            },
          ],
          usageTitle: "典型业务用途",
          usageItems: [
            "品牌关键词投放和自然流量趋势分析",
            "核心 ASIN 的点击、转化和目录表现监控",
            "复购商品识别与会员营销策略优化",
            "关联购买关系分析与套装搭配策略制定",
            "品牌周报、月报和经营复盘看板生成",
          ],
        },
        en: {
          title: "Amazon Brand Analytics Feature Overview",
          lead:
            "GO GLOBAL CREDIT LIMTED provides brand performance analytics tools for authorized Amazon brand sellers and brand owners to review search performance, repeat purchase behavior, market basket relationships, and business trends, helping brand teams optimize advertising, product selection, and content operations.",
          contactLink: "Contact Us",
          complianceLink: "View SP-API Compliance Statement",
          contactSubject: "Brand Analytics feature inquiry",
          audienceTitle: "Intended Users",
          audienceItems: [
            "Amazon sellers who have completed brand registry and hold the required data permissions",
            "Brand owners and their authorized operations teams",
            "Service teams processing data on behalf of customers after explicit authorization",
          ],
          featureTitle: "Brand Analytics Features We Provide",
          featureItems: [
            {
              title: "Search Query and Search Catalog Performance",
              desc:
                "Help brand teams review impressions, clicks, conversions, and catalog performance in Amazon search scenarios, and analyze core keywords, core ASINs, and traffic trends.",
            },
            {
              title: "Market Basket and Substitute Purchase Analysis",
              desc:
                "Identify co-purchase relationships, substitute purchase behavior, and related product opportunities to support bundling, cross-selling, and competitor strategy analysis.",
            },
            {
              title: "Repeat Purchase and Customer Retention Analysis",
              desc:
                "Track repeat purchase behavior, repurchase cycles, and customer retention trends to support membership operations, repurchase campaigns, and lifecycle marketing.",
            },
            {
              title: "Brand Reporting and Internal Dashboards",
              desc:
                "Integrate brand analytics data into internal dashboards for brand managers, operations teams, and advertising teams for routine reviews and decision-making, without offering public data query services.",
            },
          ],
          reportTitle: "Data Usage Scenarios Aligned with the Brand Analytics Role",
          reportLead:
            "After customers meet the required eligibility and provide explicit authorization, we may use the following Amazon SP-API Brand Analytics data types to deliver analytics capabilities:",
          reportItems: [
            "`GET_BRAND_ANALYTICS_SEARCH_QUERY_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_SEARCH_CATALOG_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_MARKET_BASKET_REPORT`",
            "`GET_BRAND_ANALYTICS_REPEAT_PURCHASE_REPORT`",
          ],
          reportNote:
            "Actual usage remains subject to Amazon requirements regarding customer eligibility, application role, and authorization status.",
          boundaryTitle: "Authorization Method and Data Boundaries",
          boundaryItems: [
            {
              title: "Access Only After Explicit Authorization",
              desc:
                "We access brand analytics data only after customer authorization and do not collect or view such information without authorization.",
            },
            {
              title: "Only for the Authorized Customer",
              desc:
                "Data is used only for the customer's own brand operations and internal management, not for public-facing data products.",
            },
            {
              title: "No Sale or Transfer",
              desc:
                "We do not sell, rent, transfer, license, or publicly disclose brand analytics data obtained through Amazon SP-API.",
            },
            {
              title: "No Public Cross-Customer Aggregation",
              desc:
                "We do not aggregate data from multiple sellers or brands for public display, sale, or open download.",
            },
          ],
          usageTitle: "Typical Business Uses",
          usageItems: [
            "Brand keyword advertising and organic traffic trend analysis",
            "Monitoring clicks, conversions, and catalog performance for core ASINs",
            "Identifying repeat-purchase products and improving membership marketing strategies",
            "Analyzing related purchases and defining bundle strategies",
            "Generating weekly, monthly, and business review dashboards for brand teams",
          ],
        },
      },
    };
  },
  computed: {
    locale() {
      return localeState.locale;
    },
    texts() {
      return this.copy[this.locale] || this.copy.zh;
    },
    contactHref() {
      return `mailto:keith.lu@hysenglobal.com?subject=${encodeURIComponent(
        this.texts.contactSubject
      )}`;
    },
  },
  methods: {
    cleanReportName(value) {
      return value.replace(/`/g, "");
    },
  },
};
</script>

<style scoped>
.brand-analytics-page {
  --ink: #122620;
  --paper: #f5f2e9;
  --accent: #d8944b;
  --line: rgba(18, 38, 32, 0.16);
  background: var(--paper);
  color: var(--ink);
  min-height: 100%;
}
.container {
  margin: 0 auto;
  max-width: 80rem;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}
.hero {
  background: #d9d3c5;
  overflow: hidden;
  padding: 5rem 0 0;
}
.hero-grid {
  display: grid;
  gap: 3rem;
}
.hero-copy,
.section-heading,
.report-copy,
.boundary-heading,
.usage-grid > div:first-child { text-align: left; }
.eyebrow {
  color: #8d592c;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  margin: 0 0 1.4rem;
  text-transform: uppercase;
}
h1 {
  color: var(--ink);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 2.75rem;
  font-weight: 400;
  line-height: 1.08;
  margin: 0;
}
.lead {
  color: rgba(18, 38, 32, 0.68);
  font-size: 0.96rem;
  line-height: 1.85;
  margin: 1.5rem 0 0;
  max-width: 43rem;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2rem;
}
.action-link {
  border-bottom: 1px solid var(--ink);
  color: var(--ink);
  font-size: 0.82rem;
  padding: 0.55rem 0;
  text-decoration: none;
}
.action-link.primary {
  background: var(--ink);
  border: 1px solid var(--ink);
  color: #f7f3e8;
  padding: 0.7rem 1.2rem;
}
.analytics-visual {
  background: var(--ink);
  color: #f7f3e8;
  min-height: 350px;
  padding: 1.5rem;
}
.visual-head,
.visual-foot {
  align-items: center;
  display: flex;
  font-size: 0.65rem;
  justify-content: space-between;
  letter-spacing: 0.12em;
}
.status-dot { color: #e6a15d; }
.status-dot::before {
  background: #e6a15d;
  border-radius: 50%;
  content: "";
  display: inline-block;
  height: 5px;
  margin-right: 6px;
  width: 5px;
}
.metric-row {
  border-bottom: 1px solid rgba(247, 243, 232, 0.15);
  border-top: 1px solid rgba(247, 243, 232, 0.15);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 1.5rem;
}
.metric-row div { border-right: 1px solid rgba(247, 243, 232, 0.15); padding: 1rem 0.7rem; }
.metric-row div:last-child { border-right: 0; }
.metric-row span { color: rgba(247, 243, 232, 0.45); display: block; font-size: 0.58rem; }
.metric-row strong { display: block; font-family: Georgia, "Times New Roman", serif; font-size: 1.65rem; font-weight: 400; margin-top: 0.4rem; }
.chart-area {
  align-items: end;
  border-bottom: 1px solid rgba(247, 243, 232, 0.18);
  display: flex;
  gap: 7px;
  height: 150px;
  padding-top: 1.5rem;
}
.chart-area span { background: #d8944b; flex: 1; min-height: 8px; }
.chart-area span:nth-child(3n) { background: #f1dfbd; }
.visual-foot { color: rgba(247, 243, 232, 0.4); margin-top: 0.65rem; }
.audience-strip {
  border-top: 1px solid rgba(18, 38, 32, 0.24);
  display: grid;
  gap: 1.5rem;
  margin-top: 4rem;
  padding-bottom: 2.5rem;
  padding-top: 2rem;
}
.section-label {
  color: rgba(18, 38, 32, 0.55);
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.section-label span { color: var(--accent); margin-right: 1rem; }
.audience-strip ul { display: grid; gap: 0.8rem; list-style: none; margin: 0; padding: 0; }
.audience-strip li { color: rgba(18, 38, 32, 0.7); font-size: 0.85rem; line-height: 1.6; padding-left: 1rem; position: relative; text-align: left; }
.audience-strip li::before { background: var(--accent); content: ""; height: 5px; left: 0; position: absolute; top: 0.62rem; width: 5px; }
.features-section,
.boundary-section,
.usage-section { padding: 5rem 0; }
.section-grid,
.reports-grid,
.usage-grid { display: grid; gap: 3rem; }
.section-heading h2,
.report-copy h2,
.boundary-heading h2,
.usage-grid h2 {
  color: var(--ink);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 2.25rem;
  font-weight: 400;
  line-height: 1.15;
  margin: 1.5rem 0 0;
}
.feature-list,
.boundary-grid {
  display: grid;
}
.feature-item {
  border-top: 1px solid var(--line);
  display: grid;
  gap: 0.8rem;
  grid-template-columns: 2rem 1fr;
  padding: 1.8rem 0;
}
.item-index,
.boundary-item > span { color: var(--accent); font-family: Georgia, "Times New Roman", serif; font-size: 0.8rem; grid-row: 1 / 3; }
.feature-item h3,
.boundary-item h3 {
  color: var(--ink);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.35;
  margin: 0;
  text-align: left;
}
.feature-item p,
.boundary-item p {
  color: rgba(18, 38, 32, 0.62);
  font-size: 0.88rem;
  line-height: 1.75;
  margin: 0;
  text-align: left;
}
.reports-section {
  background: var(--ink);
  color: #f7f3e8;
  padding: 5rem 0;
}
.section-label.light { color: rgba(247, 243, 232, 0.45); }
.report-copy h2 { color: #f7f3e8; }
.report-copy > p { color: rgba(247, 243, 232, 0.62); line-height: 1.8; margin: 1.5rem 0 0; text-align: left; }
.report-list {
  border-top: 1px solid rgba(247, 243, 232, 0.18);
  list-style: none;
  margin: 0;
  padding: 0;
}
.report-list li {
  align-items: flex-start;
  border-bottom: 1px solid rgba(247, 243, 232, 0.18);
  display: grid;
  gap: 1rem;
  grid-template-columns: 2rem minmax(0, 1fr);
  padding: 1.25rem 0;
  text-align: left;
}
.report-list span { color: #e6a15d; font-family: Georgia, "Times New Roman", serif; font-size: 0.78rem; }
.report-list code { color: #f7f3e8; font-family: Menlo, Monaco, Consolas, monospace; font-size: 0.78rem; line-height: 1.7; overflow-wrap: anywhere; }
.report-copy .note { color: #e6a15d; font-size: 0.78rem; }
.boundary-heading { max-width: 46rem; }
.boundary-grid { border-top: 1px solid var(--line); margin-top: 3rem; }
.boundary-item {
  border-bottom: 1px solid var(--line);
  display: grid;
  gap: 0.8rem;
  grid-template-columns: 2rem 1fr;
  padding: 1.75rem 0;
}
.usage-section { background: #d9d3c5; }
.usage-list { border-top: 1px solid rgba(18, 38, 32, 0.25); list-style: none; margin: 0; padding: 0; }
.usage-list li { border-bottom: 1px solid rgba(18, 38, 32, 0.2); color: rgba(18, 38, 32, 0.75); display: grid; font-size: 0.9rem; gap: 1rem; grid-template-columns: 2rem 1fr; line-height: 1.6; padding: 1.1rem 0; text-align: left; }
.usage-list span { color: #9a622c; font-family: Georgia, "Times New Roman", serif; font-size: 0.78rem; }
@media (min-width: 768px) {
  .audience-strip { grid-template-columns: 0.7fr 1.3fr; }
  .audience-strip ul { grid-template-columns: repeat(3, 1fr); }
  .boundary-grid { grid-template-columns: repeat(2, 1fr); }
  .boundary-item:nth-child(odd) { border-right: 1px solid var(--line); padding-right: 2rem; }
  .boundary-item:nth-child(even) { padding-left: 2rem; }
}
@media (min-width: 1024px) {
  .hero { padding-top: 7rem; }
  .hero-grid { align-items: center; grid-template-columns: 1.15fr 0.85fr; gap: 5rem; }
  h1 { font-size: 4.15rem; max-width: 780px; }
  .analytics-visual { min-height: 410px; padding: 2rem; }
  .chart-area { height: 185px; }
  .audience-strip { margin-top: 6rem; }
  .features-section,
  .boundary-section,
  .usage-section,
  .reports-section { padding: 7rem 0; }
  .section-grid { grid-template-columns: 0.75fr 1.25fr; gap: 6rem; }
  .section-heading h2,
  .report-copy h2,
  .boundary-heading h2,
  .usage-grid h2 { font-size: 3.1rem; }
  .reports-grid { grid-template-columns: 0.9fr 1.1fr; gap: 6rem; }
  .usage-grid { grid-template-columns: 0.8fr 1.2fr; gap: 5rem; }
}
@media (max-width: 560px) {
  .hero { padding-top: 4rem; }
  h1 { font-size: 2.35rem; }
  .hero-actions { align-items: flex-start; flex-direction: column; }
  .analytics-visual { min-height: 320px; }
  .metric-row strong { font-size: 1.3rem; }
  .section-heading h2,
  .report-copy h2,
  .boundary-heading h2,
  .usage-grid h2 { font-size: 2rem; }
}
</style>
